import { FC } from 'react';
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  value?: string;
  onChange: (file: File) => void;
  onRemove?: () => void;
  accept?: string;
  isUploading?: boolean;
  progress?: number;
  error?: string;
  label?: string;
  previewComponent?: React.ReactNode;
}

export const FileUpload: FC<FileUploadProps> = ({
  value,
  onChange,
  onRemove,
  accept,
  isUploading = false,
  progress = 0,
  error,
  label,
  previewComponent
}) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onChange(file);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative w-full">
          <input
            type="file"
            accept={accept}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          <div className={cn(
            "w-full p-4 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition-colors",
            isUploading ? "bg-gray-50 border-gray-300" : "hover:border-primary",
            error ? "border-red-500" : ""
          )}>
            {isUploading ? (
              <div className="flex flex-col items-center gap-2 w-full">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <div className="text-sm text-gray-500">Subiendo archivo... {progress}%</div>
                <Progress value={progress} className="h-2 w-full" />
              </div>
            ) : value ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-600">Archivo subido correctamente</span>
              </div>
            ) : (
              <>
                <Upload className="w-6 h-6 text-gray-400" />
                <div className="text-sm text-gray-500">
                  {label || "Arrastra un archivo o haz clic para seleccionar"}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {value && !isUploading && (
        <div className="flex items-center justify-between p-2 border rounded-md">
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline truncate max-w-[80%]"
          >
            {value.split('/').pop()}
          </a>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {previewComponent && value && !isUploading && (
        <div className="mt-4">
          {previewComponent}
        </div>
      )}
    </div>
  );
};
